import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateSliceModal } from './CreateSliceModal';
import { SliceCard } from './SliceCard';
import { SliceSearch } from './SliceSearch';

import { AnimateSpin } from '@/components/AnimateSpin';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { useGetMerchandisingCollection } from '@/hooks/useGetMerchandisingCollection';
import { useGetMerchandisingCollectionSlices } from '@/hooks/useGetMerchandisingCollectionSlices';
import { ConfirmRemoveSliceModal } from '@/pages/MerchandisingCollections/components/ConfirmRemoveSliceModal';
import {
  MerchandisingCollection,
  MerchandisingCollectionSlice,
  MerchandisingCollectionSlicesQueryFilterDto,
} from '@/services/MerchandisingCollectionsService';

const queryFiltersInitialState = {
  orderBy: 'createdAt',
  orderDirection: 'ASC',
} as Partial<MerchandisingCollectionSlicesQueryFilterDto>;

interface SlicesListProps {
  collection: MerchandisingCollection;
}

export function SlicesList({ collection }: SlicesListProps) {
  const [isCreateModalOpen, setIsCreateModal] = useState(false);

  const [sliceToRemove, setSliceToRemove] =
    useState<MerchandisingCollectionSlice | null>(null);

  const [queryFilters, setQueryFilters] = useState({
    orderBy: 'createdAt',
    orderDirection: 'ASC',
  } as Partial<MerchandisingCollectionSlicesQueryFilterDto>);

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Collection.components.SlicesList',
  });

  function handleOpenCreateModal() {
    setIsCreateModal(true);
  }

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModal(false);
  }, []);

  const handleOpenRemoveModal = useCallback(
    (slice: MerchandisingCollectionSlice) => {
      setSliceToRemove(slice);
    },
    [],
  );

  const handleCloseRemoveModal = useCallback(() => {
    setSliceToRemove(null);
  }, []);

  const handleQueryFilters = useCallback(
    (queryFilters: Partial<MerchandisingCollectionSlicesQueryFilterDto>) => {
      setQueryFilters(queryFilters);
    },
    [],
  );

  const { merchandisingCollectionSlices, isFetching } =
    useGetMerchandisingCollectionSlices(collection.id, queryFilters);

  const { merchandisingCollection } = useGetMerchandisingCollection(
    collection.id,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('title')} ({merchandisingCollection?.slicesCount})
        </CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>

      <CardContent>
        <div>
          <SliceSearch
            queryFiltersInitialState={queryFiltersInitialState}
            onQueryFilters={handleQueryFilters}
          />
        </div>

        {merchandisingCollectionSlices.length > 0 && (
          <div className="mt-10 space-y-4">
            <Button
              className="w-full md:w-auto"
              onClick={handleOpenCreateModal}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('createSliceButtonLabel')}
            </Button>

            <ul className="space-y-4">
              {merchandisingCollectionSlices.map((slice) => (
                <li key={slice.id}>
                  <SliceCard slice={slice} onRemove={handleOpenRemoveModal} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {isFetching && (
          <div>
            <AnimateSpin />
          </div>
        )}

        {!isFetching && merchandisingCollectionSlices.length === 0 && (
          <section className="mt-10">
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="space-y-2 text-center">
                  <h3 className="text-lg font-semibold">
                    {queryFilters.name &&
                      t('sliceNotFoundWithTerm', { name: queryFilters.name })}
                    {!queryFilters.name && t('sliceNotFound')}
                  </h3>

                  {!queryFilters.name && (
                    <p className="text-muted-foreground">
                      {t('firstSlice')}{' '}
                      <Button
                        variant="link"
                        className="p-0 font-semibold underline"
                        onClick={handleOpenCreateModal}
                      >
                        {t('firstSliceButtonLabel')}
                      </Button>
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}
      </CardContent>

      <CreateSliceModal
        collectionId={collection.id}
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
      />

      <ConfirmRemoveSliceModal
        sliceToRemove={sliceToRemove}
        onRemove={handleCloseRemoveModal}
      />
    </Card>
  );
}
